import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import { useDispatch, useSelector } from 'react-redux';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import PageContainer from '../../components/layout/PageContainer';
import {
  FBoxContainer,
  FHAccordion,
  FHButton,
  FHConfirmDialog,
  FHInputField,
  FHPageLink,
  FHRadio,
  FHRadioGroup,
  FProgressBar,
} from '../../components/base';
import { getSessionStatus } from '../../utils/common';
import { sessionActions } from '../../store';
import { Themes } from '../../constants';
import { FORTA_BLUE, FORTA_GRAY3, FORTA_WHITE } from '../../utils/colors';
import SessionReview from '../../components/layout/qa/session-review';
import { ReasonForDeletion } from '../../components/layout/qa/constants';
import RemoveIcon from '../../components/svgs/RemoveIcon';

import type { BTSessionStatus } from '../../constants';
import type { RootState } from '../../store';
import type { ChangeEvent } from 'react';

function ChangeSessionStatus() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: session, loading: sessionLoading } = useSelector(
    (state: RootState) => state.sessions,
  );

  const reviewRef = useRef(null);

  const clinical_qa_review = session?.clinical_qa_review;

  const [sessionId, setSessionId] = useState<string>('');
  const [sessionStatus, setSessionStatus] = useState<BTSessionStatus | null>(null);
  const [reasonForDeletion, setReasonForDeletion] = useState<ReasonForDeletion | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);

  useEffect(() => {
    if (!session) return;
    setSessionStatus(session.status);
  }, [session]);

  const validateSessionId = (sessionId: string) => {
    if (isNaN(Number(sessionId))) {
      toast.error('Session note ID must be a number', {
        toastId: 'session-note-status-error',
      });
      return false;
    }
    return true;
  };

  const handleGetStatus = () => {
    if (!sessionId || !validateSessionId(sessionId)) return;
    setSessionStatus(null);
    dispatch(
      sessionActions.getSession({
        sessionId: sessionId.trim(),
        getAccessTokenSilently,
      }),
    );
  };

  const handleResetStatus = () => {
    if (!sessionId || !validateSessionId(sessionId)) return;
    dispatch(
      sessionActions.changeSessionStatus({
        sessionId: sessionId.trim(),
        getAccessTokenSilently,
      }),
    );
  };

  const takeScreenshot = (name: string) => {
    if (reviewRef.current) {
      html2canvas(reviewRef.current).then((canvas) => {
        const link = document.createElement('a');
        link.download = name;
        link.href = canvas.toDataURL();
        link.click();
      });
    }
  };

  const handleDeleteSessionReview = async () => {
    const response = await dispatch(
      sessionActions.deleteClinicalReview({
        sessionId: session?.id,
        clinicalQAReviewId: session?.clinical_qa_review?.id,
        getAccessTokenSilently,
        data: {
          reason: reasonForDeletion,
          date_deleted: new Date().toISOString(),
        },
      }),
    );
    if (!response.error) {
      toast.success('CQA review deleted');
      await dispatch(
        sessionActions.getSession({
          sessionId: sessionId.trim(),
          getAccessTokenSilently,
        }),
      );
      setShowConfirmDelete(false);
    }
  };

  const renderDeleteOptions = () => {
    return (
      <Box mt={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
          <RemoveIcon />
          <Typography color={FORTA_BLUE.primary} variant="h4" sx={{ ml: 1, fontWeight: '500' }}>
            Reason for deletion
          </Typography>
        </Box>
        <Typography
          color={FORTA_BLUE.primary}
          sx={{
            mb: 3,
            fontWeight: '400',
            lineHeight: '21px',
            fontSize: '14px',
            color: FORTA_GRAY3,
          }}
        >
          Please select a reason to delete session note here
        </Typography>
        <FHRadioGroup
          rowGap="16px"
          colorTheme={Themes.Primary}
          coloredBackground
          name="reason-for-deletion"
          value={reasonForDeletion}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            const reason = e.target.value;
            if (
              reason === ReasonForDeletion.Correction ||
              reason === ReasonForDeletion.Incomplete
            ) {
              setReasonForDeletion(reason);
            }
          }}
          sx={{ flexDirection: 'row', mb: 1 }}
        >
          <FormControlLabel
            sx={{
              width: 'fit-content',
              pr: 6,
              fontSize: '16px',
              lineHeight: '24px',
              fontWeight: '400',
            }}
            value={ReasonForDeletion.Correction}
            control={<FHRadio colorTheme={Themes.Primary} />}
            label="Correction"
            data-testid="reasonForDeletionCorrectionOption"
          />
          <FormControlLabel
            sx={{ width: 'fit-content', fontSize: '16px', lineHeight: '24px', fontWeight: '400' }}
            value={ReasonForDeletion.Incomplete}
            control={<FHRadio colorTheme={Themes.Primary} />}
            label="Incomplete"
            data-testid="reasonForDeletionIncompleteOption"
          />
        </FHRadioGroup>
      </Box>
    );
  };

  const currentStatus = session ? getSessionStatus(session) : null;

  return (
    <PageContainer>
      {sessionLoading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography textAlign={'left'} variant="h4">
              Change session status
            </Typography>
          </Box>
          <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }} />
          <Box sx={{ display: 'flex', mt: 2 }}>
            <Typography textAlign={'left'} variant="h6" sx={{ width: '230px' }}>
              Enter session note ID:
            </Typography>
            <FHInputField
              inputProps={
                { 'data-testid': 'sessionIdInput' }
              }
              inputHeight={40}
              autoComplete="off"
              sx={{ boxShadow: 'none', width: 185 }}
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
            />
            {session?.bay && !sessionLoading && (
              <Box
                sx={{
                  ml: 2,
                  border: `1px solid ${FORTA_BLUE.primary}`,
                  backgroundColor: FORTA_WHITE,
                  padding: '0.5rem 0.6rem',
                  color: FORTA_BLUE.primary,
                  borderRadius: '10px',
                  height: 40,
                  display: 'inline',
                }}
              >
                {session?.bay}
              </Box>
            )}
          </Box>
          {sessionStatus && (
            <Box mt={2}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  width: 'fit-content',
                }}
              >
                <Typography textAlign={'left'} variant="h6">
                  Current session status:
                </Typography>
                <Box
                  sx={{
                    fontSize: '16px',
                    border: `1px solid ${currentStatus?.borderColor}`,
                    color: currentStatus?.color,
                    backgroundColor: FORTA_WHITE,
                    padding: '0.5rem 0.6rem',
                    borderRadius: '10px',
                  }}
                >
                  <img src={currentStatus?.icon} alt="icon" width={24} height={24} />{' '}
                  {currentStatus?.status}
                </Box>
              </Box>
            </Box>
          )}
          {session && !sessionLoading && (
            <>
              <Box sx={{ display: 'flex', mt: 2 }}>
                <Typography
                  textAlign={'left'}
                  display={'inline'}
                  variant="h6"
                  sx={{ width: '230px' }}
                >
                  Client name:
                </Typography>
                <Typography display={'inline'} variant="h6">
                  {`${session?.client?.first_name} ${session?.client?.last_name}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', mt: 2 }}>
                <Typography
                  textAlign={'left'}
                  display={'inline'}
                  variant="h6"
                  sx={{ width: '230px' }}
                >
                  Session Date:
                </Typography>
                <Typography display={'inline'} variant="h6">
                  {`${moment(session?.date_start).format('MM/DD/YY')}`}
                </Typography>
              </Box>
              {clinical_qa_review && (
                <Box sx={{}}>
                  {clinical_qa_review?.reviewing_provider && (
                    <Box
                      sx={{
                        borderRadius: '8px',
                        backgroundColor: FORTA_WHITE,
                        textAlign: 'left',
                        p: 3,
                        my: 3,
                      }}
                    >
                      <Typography color={FORTA_BLUE.primary} mb={2} fontSize={18} fontWeight={700}>
                        CQA review provider
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}
                      >
                        <Box mr={3}>
                          <Typography color={FORTA_BLUE.primary}>Name:</Typography>
                          <Typography>{`${clinical_qa_review?.reviewing_provider?.first_name} ${clinical_qa_review?.reviewing_provider?.last_name} (Id: ${clinical_qa_review?.reviewing_provider?.id})`}</Typography>
                        </Box>

                        <Box mr={3}>
                          <Typography color={FORTA_BLUE.primary}>Email:</Typography>
                          <Typography>{`${
                            clinical_qa_review?.reviewing_provider?.email ?? ''
                          }`}</Typography>
                        </Box>
                        <Box>
                          <Typography color={FORTA_BLUE.primary}>Phone:</Typography>
                          <Typography>{`${
                            clinical_qa_review?.reviewing_provider?.phone ?? ''
                          }`}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  <FHAccordion colorTheme={Themes.Primary} sx={{ boxShadow: 'none' }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        py: 2,
                        px: 3,
                        '& .MuiAccordionSummary-content': {
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        },
                      }}
                    >
                      <Box>
                        <Typography color={FORTA_BLUE.primary} fontWeight={700} fontSize={18}>
                          Preview CQA review
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                      <Box ref={reviewRef}>
                        <SessionReview isAdminView />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -5 }}>
                        <FHButton
                          onClick={() => takeScreenshot(`session-${session.id}-cqa-review.png`)}
                        >
                          Capture Screenshot
                        </FHButton>
                        <FHButton sx={{ ml: 2 }} onClick={() => setShowConfirmDelete(true)}>
                          Delete CQA review
                        </FHButton>
                      </Box>
                    </AccordionDetails>
                  </FHAccordion>

                  <FHConfirmDialog
                    open={showConfirmDelete}
                    colorTheme={Themes.Primary}
                    title={''}
                    submittext={'Submit & delete'}
                    hideCancelButton={true}
                    onClose={() => setShowConfirmDelete(false)}
                    onSubmit={handleDeleteSessionReview}
                    contentRenderer={renderDeleteOptions}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    disableSubmitButton={!reasonForDeletion}
                    dialogStyles={{ padding: '16px 24px', height: '248px' }}
                    submitButtonStyles={{
                      fontSize: '21px',
                      lineHeight: '38px',
                      fontWeight: '700',
                      padding: '8px 16px',
                    }}
                    submitBtnTestId={'submitReviewDelete'}
                  />
                </Box>
              )}
            </>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mt: 2,
              gap: 2,
            }}
          >
            <FHButton
              data-testid="getStatusButton"
              variant="contained"
              sx={{ mt: 2, width: '200px' }}
              onClick={handleGetStatus}
              disabled={sessionLoading || !sessionId}
            >
              Get status
            </FHButton>
            <FHButton
              data-testid="resetStatusButton"
              variant="contained"
              sx={{ mt: 2, width: '200px' }}
              onClick={handleResetStatus}
              disabled={sessionLoading || !sessionId || !sessionStatus}
            >
              Reset status
            </FHButton>
          </Box>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default ChangeSessionStatus;
